import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useMatch } from 'react-router-dom';
import { Header } from '../Header';
import { AdminView } from './AdminView';
import { WatchlistEditor } from '../WatchlistEditor';
import { HeatmapsView } from './HeatmapsView';
import { NavDrawer } from './NavDrawer';
import { TrinityView } from './TrinityView';
import { useHeatmapStream } from '../../hooks/useHeatmapStream';
import { useSymbolUniverse } from '../../hooks/useSymbolUniverse';
import { useWatchlistSelection } from '../../hooks/useWatchlistSelection';
import {
  DEFAULT_HEATMAP_VIEW_PRESET,
  getHeatmapViewPresetFromSearch,
} from '../../lib/heatmapViewPreset';
import { getHeatmapKey, useHeatmapStore, type Ticker } from '../../store/heatmapStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import type { AuthRevalidationFn } from '../../lib/auth';
import type { SymbolUniverse } from '../../lib/symbols';
import {
  DEFAULT_SELECTED_TICKER,
  readSelectedTickerPreference,
  writeSelectedTickerPreference,
} from '../../lib/selectedTickerPreference';
import { endInitialHeatmapTiming } from '../../lib/timing';

interface AuthenticatedAppShellProps {
  userId: string | null;
  userEmail: string | null;
  accessLevel: string | null;
  isAdmin?: boolean;
  onLogout: () => void;
  onAuthRevalidationRequested: AuthRevalidationFn;
}

export function AuthenticatedAppShell({
  userId,
  userEmail,
  isAdmin = false,
  onLogout,
  onAuthRevalidationRequested,
}: AuthenticatedAppShellProps) {
  // useMatch mirrors the router's own matching, so the header, stream, and the
  // rendered route never disagree (e.g. /trinity/unknown is Heatmaps, not Trinity).
  const isTrinityRoute = useMatch('/trinity') != null;
  const isAdminRoute = useMatch('/admin') != null;
  const [isWatchlistEditorOpen, setIsWatchlistEditorOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [streamAllSymbols, setStreamAllSymbols] = useState(false);
  const [loadWatchlists, setLoadWatchlists] = useState(false);
  const [activeTicker, setActiveTicker] = useState<Ticker>(() =>
    readSelectedTickerPreference(userId),
  );
  const activeType = useWorkspaceStore((state) => state.activeType);
  const trinityPanels = useWorkspaceStore((state) => state.trinityPanels);
  const reconcileTrinityPanelsWithEnabledTickers = useWorkspaceStore(
    (state) => state.reconcileTrinityPanelsWithEnabledTickers,
  );
  const applySymbolUniverse = useCallback(
    (universe: SymbolUniverse) => {
      if (!universe.tickerUniverseExpansionEnabled) {
        setLoadWatchlists(false);
      }
      setActiveTicker((currentTicker) => {
        if (universe.symbols.some((symbol) => symbol.ticker === currentTicker)) {
          return currentTicker;
        }
        return (
          universe.symbols.find((symbol) => symbol.ticker === DEFAULT_SELECTED_TICKER)?.ticker ??
          universe.symbols[0]?.ticker ??
          currentTicker
        );
      });
      reconcileTrinityPanelsWithEnabledTickers(universe.symbols.map((symbol) => symbol.ticker));
    },
    [reconcileTrinityPanelsWithEnabledTickers],
  );
  const {
    symbolUniverse,
    initialLoadError: symbolLoadError,
    refreshSymbolUniverse,
  } = useSymbolUniverse(onAuthRevalidationRequested, applySymbolUniverse);
  const symbols = useMemo(() => symbolUniverse?.symbols ?? [], [symbolUniverse]);
  const tickerUniverseExpansionEnabled = symbolUniverse?.tickerUniverseExpansionEnabled ?? false;
  const rightRailDashboardEnabled = symbolUniverse?.rightRailDashboardEnabled ?? false;
  const {
    watchlistState,
    watchlists,
    activeWatchlistId,
    activeSymbols,
    selectedTicker,
    symbolCounts: activeWatchlistSymbolCounts,
    selectWatchlist,
  } = useWatchlistSelection({
    userId,
    activeTicker,
    symbols,
    enabled: tickerUniverseExpansionEnabled,
    loadPersonalWatchlists: loadWatchlists,
    onTickerChange: setActiveTicker,
    onAuthRevalidationRequested,
  });
  const connectionStatus = useHeatmapStore((state) => state.connectionStatus);
  const heatmaps = useHeatmapStore((state) => state.heatmaps);
  const heatmapViewPreset = useMemo(
    () =>
      typeof window === 'undefined'
        ? DEFAULT_HEATMAP_VIEW_PRESET
        : getHeatmapViewPresetFromSearch(window.location.search),
    [],
  );

  // Until the SSE bootstrap settles, stream only what the visible view needs so the
  // selected ticker (heatmaps) or panels (trinity) paint immediately, without waiting
  // for the catalog. Afterwards, warm every enabled ticker in the background so
  // switching tickers/panels is instant (#151 "selected first, all live").
  const selectedStreamSymbols = useMemo(
    () =>
      isTrinityRoute ? [...new Set(trinityPanels.map((panel) => panel.ticker))] : [selectedTicker],
    [isTrinityRoute, selectedTicker, trinityPanels],
  );
  const symbolTickerKey = symbols.map((symbol) => symbol.ticker).join(',');
  const allStreamSymbols = useMemo(
    () => symbolTickerKey.split(',').filter(Boolean),
    [symbolTickerKey],
  );
  const streamSymbols =
    streamAllSymbols && allStreamSymbols.length > 1 ? allStreamSymbols : selectedStreamSymbols;

  const headerUpdatedAt = useMemo(() => {
    const viewKeys = isTrinityRoute
      ? trinityPanels.map((panel) => getHeatmapKey(panel.ticker, panel.type))
      : [getHeatmapKey(selectedTicker, activeType)];
    const timestamps = viewKeys
      .map((key) => heatmaps[key]?.updatedAt)
      .filter((updatedAt): updatedAt is string => updatedAt != null);

    if (timestamps.length === 0) {
      return null;
    }

    return timestamps.reduce((latest, updatedAt) =>
      Date.parse(updatedAt) > Date.parse(latest) ? updatedAt : latest,
    );
  }, [activeType, heatmaps, isTrinityRoute, selectedTicker, trinityPanels]);

  const closeNav = useCallback(() => setIsNavOpen(false), []);
  const handleBootstrapSettled = useCallback(
    (settledSymbols: readonly string[]) => {
      if (
        streamAllSymbols &&
        settledSymbols.length === allStreamSymbols.length &&
        settledSymbols.every((ticker, index) => ticker === allStreamSymbols[index])
      ) {
        setLoadWatchlists(true);
        return;
      }

      setStreamAllSymbols(true);
      if (allStreamSymbols.length <= 1) {
        setLoadWatchlists(true);
      }
    },
    [allStreamSymbols, streamAllSymbols],
  );

  useHeatmapStream(true, streamSymbols, refreshSymbolUniverse, handleBootstrapSettled);

  useEffect(() => {
    writeSelectedTickerPreference(userId, selectedTicker);
  }, [userId, selectedTicker]);

  // First heatmap for the active view is on screen once any timestamp is present;
  // ends the auth→first-heatmap timing started in the auth gate (idempotent).
  useEffect(() => {
    if (headerUpdatedAt != null) {
      endInitialHeatmapTiming();
    }
  }, [headerUpdatedAt]);

  return (
    <div
      className="dark flex h-screen min-h-screen flex-col overflow-hidden bg-background"
      style={{ minHeight: '100svh', height: '100dvh' }}
    >
      <NavDrawer
        isOpen={isNavOpen}
        onClose={closeNav}
        showAdminLink={isAdmin && tickerUniverseExpansionEnabled}
        userEmail={userEmail}
        onLogout={onLogout}
      />

      <div className="sticky top-0 z-40 bg-background">
        <Header
          connectionStatus={connectionStatus}
          updatedAt={headerUpdatedAt}
          sectionLabel={isTrinityRoute ? 'TRINITY' : isAdminRoute ? 'ADMIN' : 'HEATMAPS'}
          onNavToggle={() => setIsNavOpen((current) => !current)}
        />

        {watchlistState.collection != null && (
          <WatchlistEditor
            isOpen={isWatchlistEditorOpen}
            activeWatchlistId={activeWatchlistId}
            activeTicker={selectedTicker}
            collection={watchlistState.collection}
            catalog={symbols}
            isMutating={watchlistState.isMutating}
            error={watchlistState.mutationError}
            onClose={() => {
              watchlistState.clearMutationError();
              setIsWatchlistEditorOpen(false);
            }}
            onCreate={watchlistState.create}
            onRename={watchlistState.rename}
            onDelete={watchlistState.remove}
            onReorder={watchlistState.reorder}
            onReplaceSymbols={watchlistState.replaceSymbols}
          />
        )}
      </div>

      {symbolLoadError != null && headerUpdatedAt == null ? (
        // A catalog failure only blocks the view when there is nothing to show.
        // If SSE has already delivered a heatmap for the active view, keep the
        // live/cached data on screen (the catalog only powers the ticker picker).
        <div className="flex flex-1 items-center justify-center px-4 text-center">
          <span className="font-mono text-[11px] text-neutral-500">{symbolLoadError}</span>
        </div>
      ) : (
        <Routes>
          <Route
            path="/admin"
            element={
              // isAdmin is known synchronously; the feature flag arrives with the
              // symbol universe, so only redirect on the flag once it has loaded.
              !isAdmin || (symbolUniverse != null && !tickerUniverseExpansionEnabled) ? (
                <Navigate to="/" replace />
              ) : (
                <AdminView onSymbolsChanged={refreshSymbolUniverse} />
              )
            }
          />
          <Route
            path="/trinity"
            element={
              <TrinityView
                symbols={symbols}
                visibleStrikeCount={heatmapViewPreset.visibleStrikeCount}
              />
            }
          />
          <Route
            path="*"
            element={
              <HeatmapsView
                ticker={selectedTicker}
                symbols={activeSymbols}
                watchlists={watchlists}
                activeWatchlistId={activeWatchlistId}
                activeWatchlistSymbolCounts={activeWatchlistSymbolCounts}
                showWatchlists={tickerUniverseExpansionEnabled}
                watchlistsLoaded={watchlistState.collection != null}
                watchlistLoadError={watchlistState.loadError}
                heatmapViewPreset={heatmapViewPreset}
                rightRailDashboardEnabled={rightRailDashboardEnabled}
                onTickerChange={setActiveTicker}
                onAuthRevalidationRequested={onAuthRevalidationRequested}
                onWatchlistChange={selectWatchlist}
                onManageWatchlists={() => setIsWatchlistEditorOpen(true)}
                onRetryWatchlists={watchlistState.retryLoad}
              />
            }
          />
        </Routes>
      )}
    </div>
  );
}
